/* eslint-disable */

import React, { useState, useEffect, useMemo } from 'react'
import { Card, CardContent, Typography, Box, IconButton, Divider, Button, Dialog, DialogTitle, DialogContent, TextField, CardMedia } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import CloseIcon from '@mui/icons-material/Close'
import SkeinIcon from './SkeinIcon'
import ColorwayDot from './ColorwayDot'

interface Yarn {
  id: number
  name: string
  brand: string
  material?: string
  fiber?: string
  image_url?: string | null
}

interface Colorway {
  id: number
  name: string
  hex_color: string
  hex_colors?: string[]
  yarn?: Yarn
}

interface UserYarn {
  id: number
  colorway_id: number
  dye_lot: string
  quantity: number
  colorway?: Colorway
}

interface YarnGroupCardProps {
  yarn: Yarn
  userYarns: UserYarn[]
  onDelete: (id: number) => void
  onUpdate?: (id: number, quantity: number) => Promise<void>
  onAddDyeLot?: (colorwayId: number, dyeLot: string, quantity: number) => Promise<void>
  onAddColorway?: (yarnId: number) => void
  open?: boolean
  onOpen?: () => void
  onClose?: () => void
}

const titleize = (str: string): string => {
  return str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase())
}

export default function YarnGroupCard({ yarn, userYarns, onDelete, onUpdate, onAddDyeLot, onAddColorway, open: openProp, onOpen, onClose }: YarnGroupCardProps) {
  const [openLocal, setOpenLocal] = useState(false)
  const isControlled = openProp !== undefined
  const open = isControlled ? openProp : openLocal
  const handleOpen = () => { if (isControlled) onOpen?.(); else setOpenLocal(true) }
  const handleClose = () => { if (isControlled) onClose?.(); else setOpenLocal(false) }
  const [addingDyeLotForColorway, setAddingDyeLotForColorway] = useState<number | null>(null)
  const [newDyeLot, setNewDyeLot] = useState({ dye_lot: '', quantity: 1 })
  const [adding, setAdding] = useState(false)
  const [imageLoadFailed, setImageLoadFailed] = useState(false)
  const titleizedBrand = yarn.brand ? titleize(yarn.brand) : ''

  // Filter out userYarns with quantity 0
  const activeUserYarns = useMemo(() => {
    return userYarns.filter(uy => uy.quantity > 0)
  }, [userYarns])

  // Group userYarns by colorway to show "Add Dye Lot" button for each colorway
  const colorwayGroups = useMemo(() => {
    const groups = new Map<number, UserYarn[]>()
    activeUserYarns.forEach((userYarn) => {
      if (userYarn.colorway) {
        const colorwayId = userYarn.colorway.id
        if (!groups.has(colorwayId)) {
          groups.set(colorwayId, [])
        }
        groups.get(colorwayId)!.push(userYarn)
      }
    })
    return groups
  }, [activeUserYarns])

  // Get unique colorways for preview circles
  const uniqueColorways = useMemo(() => {
    const colorways = new Map<number, Colorway>()
    activeUserYarns.forEach((userYarn) => {
      if (userYarn.colorway) {
        colorways.set(userYarn.colorway.id, userYarn.colorway)
      }
    })
    return Array.from(colorways.values())
  }, [activeUserYarns])

  // Reset form state when modal closes
  useEffect(() => {
    if (!open) {
      setAddingDyeLotForColorway(null)
      setNewDyeLot({ dye_lot: '', quantity: 1 })
    }
  }, [open])

  const showImage = yarn.image_url && !imageLoadFailed

  return (
    <>
      <Box onClick={handleOpen} sx={{ cursor: 'pointer' }}>
        {showImage && (
          <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'center', bgcolor: 'grey.50', borderRadius: 2, p: 1.5 }}>
            <CardMedia
              component="img"
              image={yarn.image_url!}
              alt={yarn.name}
              onError={() => setImageLoadFailed(true)}
              sx={{ maxHeight: 140, objectFit: 'contain' }}
            />
          </Box>
        )}
        <Card
          sx={{
            borderRadius: 3,
            bgcolor: '#FDFBF7',
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
            '&:hover': { boxShadow: 3 }
          }}
        >
          <Box sx={{ height: 6, bgcolor: 'primary.main', opacity: 0.3 }} />
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="overline"
                  sx={{ letterSpacing: 1.5, opacity: 0.8, fontSize: '0.7rem' }}
                >
                  {titleizedBrand}
                </Typography>
                <Typography
                  variant="h6"
                  component="h2"
                  sx={{ fontFamily: 'Georgia, serif', fontWeight: 600, lineHeight: 1.3 }}
                >
                  {yarn.name}
                </Typography>
                {(yarn.material || yarn.fiber) && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {yarn.material || yarn.fiber}
                  </Typography>
                )}
                {uniqueColorways.length > 0 && (
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap', mt: 1 }}>
                    {uniqueColorways.map((colorway) => (
                      <ColorwayDot
                        key={colorway.id}
                        colors={colorway.hex_colors?.length ? colorway.hex_colors : [colorway.hex_color || '#000']}
                        size={16}
                        title={colorway.name}
                        variant="pie"
                      />
                    ))}
                  </Box>
                )}
              </Box>
              {onAddColorway && (
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={(e) => {
                    e.stopPropagation()
                    onAddColorway(yarn.id)
                  }}
                  sx={{ textTransform: 'none', ml: 1 }}
                >
                  Colorway
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>

      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h5" component="span">
                {yarn.name}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary" component="div">
                {titleizedBrand}
              </Typography>
              {yarn.material && (
                <Typography variant="body2" color="text.secondary" component="div">
                  {yarn.material}
                </Typography>
              )}
            </Box>
            <IconButton
              onClick={handleClose}
              sx={{ ml: 2 }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {activeUserYarns.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              No active colorways (all quantities are 0)
            </Typography>
          ) : (
            Array.from(colorwayGroups.entries()).map(([colorwayId, colorwayUserYarns], colorwayIndex) => {
            const isLastColorway = colorwayIndex === colorwayGroups.size - 1
            const colorway = colorwayUserYarns[0]?.colorway
            return (
              <Box key={colorwayId}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  {colorway && (
                    <SkeinIcon 
                      color={colorway.hex_colors?.length ? colorway.hex_colors : (colorway.hex_color || '#000')} 
                      size={60}
                    />
                  )}
                  <Typography variant="body1" fontWeight="bold">
                    {colorway?.name || 'Unknown Colorway'}
                  </Typography>
                </Box>
                {colorwayUserYarns.map((userYarn, index) => (
                  <ColorwayRow
                    key={userYarn.id}
                    userYarn={userYarn}
                    onDelete={onDelete}
                    onUpdate={onUpdate}
                    showDivider={index < colorwayUserYarns.length - 1 || !isLastColorway}
                  />
                ))}
                {onAddDyeLot && (
                  <Box sx={{ pb: 1, pl: 9 }}>
                    {addingDyeLotForColorway === colorwayId ? (
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                        <TextField
                          size="small"
                          label="Dye Lot"
                          value={newDyeLot.dye_lot}
                          onChange={(e) => setNewDyeLot({ ...newDyeLot, dye_lot: e.target.value })}
                          sx={{ flex: 1, minWidth: 120 }}
                          disabled={adding}
                        />
                        <TextField
                          size="small"
                          label="Quantity"
                          type="number"
                          value={newDyeLot.quantity}
                          onChange={(e) => setNewDyeLot({ ...newDyeLot, quantity: parseInt(e.target.value) || 1 })}
                          inputProps={{ min: 1 }}
                          sx={{ width: 100 }}
                          disabled={adding}
                        />
                        <Button
                          size="small"
                          variant="contained"
                          onClick={async () => {
                            if (!newDyeLot.dye_lot.trim()) return
                            setAdding(true)
                            try {
                              await onAddDyeLot(colorwayId, newDyeLot.dye_lot, newDyeLot.quantity)
                              setAddingDyeLotForColorway(null)
                              setNewDyeLot({ dye_lot: '', quantity: 1 })
                            } catch (error) {
                              console.error('Error adding dye lot:', error)
                            } finally {
                              setAdding(false)
                            }
                          }}
                          disabled={adding || !newDyeLot.dye_lot.trim()}
                        >
                          Add
                        </Button>
                        <Button
                          size="small"
                          onClick={() => {
                            setAddingDyeLotForColorway(null)
                            setNewDyeLot({ dye_lot: '', quantity: 1 })
                          }}
                          disabled={adding}
                        >
                          Cancel
                        </Button>
                      </Box>
                    ) : (
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => setAddingDyeLotForColorway(colorwayId)}
                        sx={{ textTransform: 'none' }}
                      >
                        Add Dye Lot
                      </Button>
                    )}
                  </Box>
                )}
                {!isLastColorway && <Divider sx={{ my: 1 }} />}
              </Box>
            )
          })
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}

interface ColorwayRowProps {
  userYarn: UserYarn
  onDelete: (id: number) => void
  onUpdate?: (id: number, quantity: number) => Promise<void>
  showDivider: boolean
}

function ColorwayRow({ userYarn, onDelete, onUpdate, showDivider }: ColorwayRowProps) {
  const [quantity, setQuantity] = useState(userYarn.quantity)
  const [updating, setUpdating] = useState(false)

  // Sync quantity state when userYarn prop changes
  useEffect(() => {
    setQuantity(userYarn.quantity)
  }, [userYarn.quantity])

  const handleQuantityChange = async (newQuantity: number) => {
    if (newQuantity < 0) return // Don't allow quantity less than 0
    
    const previousQuantity = quantity
    setQuantity(newQuantity) // Optimistic update
    
    if (onUpdate) {
      setUpdating(true)
      try {
        await onUpdate(userYarn.id, newQuantity)
      } catch (error: any) {
        console.error('Error updating quantity:', error)
        // Revert on error
        setQuantity(previousQuantity)
      } finally {
        setUpdating(false)
      }
    }
  }

  const handleIncrement = () => {
    handleQuantityChange(quantity + 1)
  }

  const handleDecrement = () => {
    handleQuantityChange(quantity - 1)
  }

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1, pl: 9 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Dye Lot: {userYarn.dye_lot || 'N/A'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton 
            size="small" 
            onClick={handleDecrement} 
            disabled={updating || quantity <= 0}
            sx={{ border: '1px solid', borderColor: 'divider' }}
          >
            <RemoveIcon fontSize="small" />
          </IconButton>
          <Typography variant="body2" sx={{ minWidth: '30px', textAlign: 'center' }}>
            {quantity}
          </Typography>
          <IconButton 
            size="small" 
            onClick={handleIncrement} 
            disabled={updating}
            sx={{ border: '1px solid', borderColor: 'divider' }}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
      {showDivider && <Divider sx={{ my: 1 }} />}
    </>
  )
}

